import { z } from 'zod';
import { diffDays, formatShortFr, isoWeekday, parseIsoDate } from '../dates.js';
import { TASK_DESCRIPTION_MAX, TASK_STEP_LABEL_MAX, TASK_STEPS_MAX } from '../tasks.js';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';
import { FEEDBACK_ATTACHMENT_TYPES } from './feedback.js';
import { ActionConsequence, PlanKind } from './plans.js';

/**
 * « À faire » (CDC §3.5) : actions proposées par les règles, tâches ajoutées à la main, étapes de projet.
 * Contrat des routes `GET/POST/PATCH /api/actions`, `GET /api/actions/done`, `GET /api/actions/stakes`,
 * `GET /api/clients/:id/messages` et `POST /api/drafts/email` (lot C1), utilisé par l'écran À faire (lot C2).
 */

export const ActionType = z.enum([
  'quote_followup',
  'invoice_create',
  'subscription_setup',
  'unpaid_reminder',
  'payment_failed',
  'review_request',
  'quote_to_send',
  'meeting',
  'deadline',
  'info',
  'project_step',
  'project_plan',
  'user_task',
  'expense_categorize',
  'partner_missing',
  'idea',
  /** Lot X : « Fixer le R2 · <client> », R1 passé sans R2 ni devis (règle `meeting_r2_missing`). */
  'meeting_r2',
  /** Lot W : « Apporteurs Axonaut à reconnaître : N noms » (champ Axonaut « Apporteur d'affaire » inconnu du Cockpit). */
  'partner_unknown',
  /** Lot AO : « Compléter le R1 de <sujet> », infos demandées avant un R1 (règle `r1_prep`). */
  'r1_prep',
  /** Lot AT : « Préparer le devis de <prospect> », R1 passé sans devis (règle `quote_prepare`). */
  'quote_prepare',
  /**
   * Lot BB (contrat) : « Support · <client> », une conversation Crisp ou un mail qui attend une réponse (règle
   * `support_conversation`, cartes créées par le lot BD). `payload.conversationId` : la conversation.
   */
  'support',
  /** Lot BB (contrat) : « <Fournisseur> · <objet> », mail important d'un fournisseur (règle `supplier_alert`, lot BG). */
  'supplier_alert',
  /** Lot BL : « Chance de signature · <prospect> », le lendemain d'un R2 passé (règle `quote_win_chance`). */
  'quote_win_chance',
  /** Lot BT : « Relancer <client> · <ce qui est attendu> », un mail qui attend une réponse (règle `mail_followup`). */
  'mail_followup',
]);
export type ActionType = z.infer<typeof ActionType>;

export const ActionStatus = z.enum(['OPEN', 'WAITING_REPLY', 'REPLY_TO_HANDLE', 'DONE', 'DISMISSED']);
export type ActionStatus = z.infer<typeof ActionStatus>;
/** Statuts d'une action encore à traiter (affichée dans la liste). */
export const OPEN_ACTION_STATUSES = [
  'OPEN',
  'WAITING_REPLY',
  'REPLY_TO_HANDLE',
] as const satisfies readonly ActionStatus[];

export const ActionOrigin = z.enum(['RULE', 'USER', 'PROJECT', 'SYSTEM']);
export type ActionOrigin = z.infer<typeof ActionOrigin>;

/** Catégories d'affichage (CDC §3.5). */
export const ActionCategory = z.enum(['projets', 'clients', 'perso']);
export type ActionCategory = z.infer<typeof ActionCategory>;

export function actionCategory(type: ActionType): ActionCategory {
  if (type === 'project_step' || type === 'project_plan' || type === 'meeting') return 'projets';
  if (type === 'user_task') return 'perso';
  return 'clients';
}

/** Règles v1 (CDC §3.5) : `rule_key` des actions d'origine RULE. */
export const RuleKey = z.enum([
  'quote_followup',
  'invoice_create.deposit',
  'invoice_create.balance',
  'subscription_setup',
  'unpaid_reminder',
  'payment_failed',
  'review_request',
  'quote_to_send',
  'deadline',
  'info.anniversary',
  'project_plan',
  'expense_categorize',
  'partner_missing',
  'project_step',
  'meeting_schedule',
  /** Lot N : facture d'un abonnement hors Stripe à faire N jours avant sa date de facturation (type `invoice_create`). */
  'manual_subscription_invoice',
  /** Lot X : R1 passé depuis `r2Missing.days` jours sans R2 du client dans l'agenda ni devis (type `meeting_r2`). */
  'meeting_r2_missing',
  /** Lot W : nom d'apporteur tapé dans Axonaut qu'aucun apporteur du Cockpit ne porte (type `partner_unknown`). */
  'partner_unknown',
  /** Lot AO : `r1Briefs.askDaysBefore` jours avant un R1, une carte par membre présent (type `r1_prep`). */
  'r1_prep',
  /** Lot AT : R1 passé sans devis ni brouillon créé ou écarté, une carte par membre présent (type `quote_prepare`). */
  'quote_prepare',
  /** Lot BB (contrat, cartes du lot BD) : conversation de support analysée qui attend une réponse (type `support`). */
  'support_conversation',
  /** Lot BB (contrat, cartes du lot BG) : mail important d'un fournisseur, par motif et domaine (type `supplier_alert`). */
  'supplier_alert',
  /** Lot BL : le lendemain d'un R2 passé, chance de signature du devis en attente du client (type `quote_win_chance`). */
  'quote_win_chance',
  /** Lot BT : mail envoyé à un client, resté sans réponse (type `mail_followup`). */
  'mail_followup',
]);
export type RuleKey = z.infer<typeof RuleKey>;

/** Libellés de la maquette par type (puce de l'action). */
export const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  quote_followup: 'Relancer',
  invoice_create: 'Facturer',
  subscription_setup: 'Abonnement',
  unpaid_reminder: 'Impayé',
  payment_failed: 'Paiement refusé',
  review_request: 'Avis Google',
  quote_to_send: 'Devis à faire',
  meeting: 'Rendez-vous',
  deadline: 'Échéance',
  info: 'Pour info',
  project_step: 'Projet',
  project_plan: 'Planifier',
  user_task: 'Tâche',
  expense_categorize: 'Dépenses',
  partner_missing: 'Apporteur',
  idea: 'Idée',
  meeting_r2: 'R2 à fixer',
  partner_unknown: 'Apporteurs',
  r1_prep: 'R1',
  quote_prepare: 'Devis à préparer',
  support: 'Support',
  supplier_alert: 'Fournisseur',
  quote_win_chance: 'Devis',
  mail_followup: 'Relancer',
};

/**
 * Bouton principal d'une action (maquette, `TYPES`) : un plan à préparer (`POST /api/plans`), un écran
 * à ouvrir, des liens seulement (abonnement : rien n'est exécuté côté Stripe), ou rien (« Fait »/« Vu »).
 * « Fait » sur une action simple passe par le plan `action.done` (CDC §3.4, actions directes).
 */
export const ActionPrimary = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('plan'), planKind: PlanKind, label: z.string() }),
  z.object({ kind: z.literal('goto'), screen: z.string(), label: z.string() }),
  z.object({ kind: z.literal('links'), label: z.string() }),
  /**
   * Lot AO : un formulaire ouvert sous la carte (« Compléter » : infos avant le R1), sans plan. Lot AT :
   * `quote_draft`, le brouillon de devis (grand volet), dont la validation passe par le plan `quote.create`.
   */
  z.object({ kind: z.literal('panel'), panel: z.enum(['r1_prep', 'quote_draft']), label: z.string() }),
  z.object({ kind: z.literal('none') }),
]);
export type ActionPrimary = z.infer<typeof ActionPrimary>;

export function actionPrimary(
  type: ActionType,
  status: ActionStatus,
  ruleKey?: string | null,
  /** Lot U : `payload.existingProjectId` d'un `project_plan` → « Rattacher le devis au projet ». */
  payload?: Record<string, unknown>,
): ActionPrimary {
  if (status === 'REPLY_TO_HANDLE') return { kind: 'none' };
  // Lot K : « Fixer le R3 avec … » (type `meeting`, règle `meeting_schedule`) ouvre le plan `meeting.schedule`.
  if (ruleKey === 'meeting_schedule') {
    return { kind: 'plan', planKind: 'meeting.schedule', label: 'Fixer le rendez-vous' };
  }
  switch (type) {
    case 'quote_followup':
      return {
        kind: 'plan',
        planKind: 'reminder.email',
        label: status === 'WAITING_REPLY' ? 'Relancer maintenant' : 'Préparer la relance',
      };
    case 'mail_followup':
      // Lot BT : la fenêtre mail habituelle, avec le brouillon préparé, dans le fil du mail d'origine.
      return { kind: 'plan', planKind: 'reminder.email', label: 'Préparer la relance' };
    case 'unpaid_reminder':
      return { kind: 'plan', planKind: 'reminder.email', label: 'Préparer le rappel' };
    case 'payment_failed':
      return { kind: 'plan', planKind: 'reminder.email', label: 'Prévenir le client' };
    case 'review_request':
      return { kind: 'plan', planKind: 'reminder.email', label: 'Préparer la demande' };
    case 'invoice_create':
      // Aucune facture n'est créée par l'API (CDC §0) : le plan ouvre Axonaut puis marque l'action facturée.
      return { kind: 'plan', planKind: 'invoice.create', label: 'Facturer dans Axonaut' };
    case 'quote_to_send':
    case 'quote_prepare':
      // Lot AT : le brouillon de devis (notes, proposition de Claude, lignes, client), puis le plan `quote.create`.
      return { kind: 'panel', panel: 'quote_draft', label: 'Préparer le devis' };
    case 'meeting':
      return { kind: 'plan', planKind: 'meeting.propose', label: 'Proposer des créneaux' };
    case 'meeting_r2':
      // Lot AF : poser le R2 dans l'agenda (créneau libre, titre des Réglages, invitation décochable).
      return { kind: 'plan', planKind: 'meeting.scheduleR2', label: 'Fixer le R2' };
    case 'subscription_setup':
      return { kind: 'links', label: "Mettre l'abonnement en place" };
    case 'expense_categorize':
      return { kind: 'goto', screen: 'depenses', label: 'Catégoriser' };
    case 'partner_missing':
      // Lot W : le canal et l'apporteur se renseignent dans la fiche Axonaut (rien n'est écrit par le Cockpit).
      return { kind: 'goto', screen: 'apporteurs', label: 'Voir les fiches à compléter' };
    case 'partner_unknown':
      // Lot W : liste « Noms d'apporteurs inconnus » de l'écran Apporteurs (créer, rattacher, ignorer).
      return { kind: 'goto', screen: 'apporteurs', label: 'Voir les noms' };
    case 'project_plan':
      // Lot U : le client a déjà un projet en cours sans devis (importé du Sheet) : on rattache le devis.
      return typeof payload?.existingProjectId === 'string'
        ? { kind: 'plan', planKind: 'project.linkQuote', label: 'Rattacher au projet' }
        : { kind: 'goto', screen: 'projets', label: 'Planifier le projet' };
    case 'project_step':
      return { kind: 'goto', screen: 'projets', label: 'Voir le projet' };
    case 'idea':
      return { kind: 'goto', screen: 'conseiller', label: 'Voir la piste' };
    case 'r1_prep':
      // Lot AO : infos avant le R1, saisies sous la carte (geste interne, rien n'est écrit ailleurs).
      return { kind: 'panel', panel: 'r1_prep', label: 'Compléter' };
    case 'support':
      // Lot BB : l'onglet Support, conversation ouverte (`/support?c=<payload.conversationId>`).
      return { kind: 'goto', screen: 'support', label: 'Ouvrir la conversation' };
    case 'supplier_alert':
      // Lot BB : l'onglet Support, vue Fournisseurs (`/support/fournisseurs`).
      return { kind: 'goto', screen: 'support', label: 'Ouvrir' };
    case 'quote_win_chance':
      // Lot BL : pas de bouton principal, les puces de la carte (10 à 90 %, « Je ne sais pas encore ») la ferment
      // d'un clic (`POST /api/actions/:id/win-chance`, geste interne, rien ne part).
      return { kind: 'none' };
    default:
      return { kind: 'none' };
  }
}

/**
 * Action secondaire d'une carte. Lot X : « Pas de R2 pour ce client » sur « Fixer le R2 »
 * (`POST /api/actions/:id/skip-r2`, geste interne, sans plan) : le R1 est marqué sans R2, la carte se ferme et
 * « Envoyer le devis » arrive aussitôt quand le devis suit le R2. Lot U, sur une carte `project_plan` :
 * « Pas de projet pour ce devis » (plan `project.ignoreQuote`) sur « Préparer le projet », « C'est un autre
 * projet » (`/projets?prepare=<devis>`) sur « Rattacher le devis au projet ». Lot AX : « Devis déjà fait » sur
 * « Préparer le devis » (`POST /api/actions/:id/quote-done`, geste interne : client, puis devis choisi).
 */
export type ActionSecondary =
  | { key: 'skip_r2'; label: string }
  | { key: 'quote_done'; label: string; r1Key: string }
  | { key: 'no_subscription'; label: string }
  | { key: 'ignore_quote'; label: string; quoteId: string }
  | { key: 'other_project'; label: string; quoteId: string };

export function actionSecondary(action: {
  status: ActionStatus;
  ruleKey?: string | null;
  type?: ActionType;
  payload?: Record<string, unknown>;
}): ActionSecondary | null {
  const open = (OPEN_ACTION_STATUSES as readonly ActionStatus[]).includes(action.status);
  if (!open) return null;
  if (action.ruleKey === 'meeting_r2_missing') {
    return { key: 'skip_r2', label: 'Pas de R2 pour ce client' };
  }
  // Lot AI [30] : remplace « Écarter » sur « Mettre en place l'abonnement » (trois raisons, `NoSubscriptionBody`).
  if (action.ruleKey === 'subscription_setup') {
    return { key: 'no_subscription', label: 'Pas d’abonnement à mettre' };
  }
  // Lot AX : un devis existe déjà dans Axonaut (client non reconnu sur le R1) : on le désigne à la main.
  const r1Key = action.payload?.r1Key;
  if (action.ruleKey === 'quote_prepare' && typeof r1Key === 'string') {
    return { key: 'quote_done', label: 'Devis déjà fait', r1Key };
  }
  const quoteId = action.payload?.quoteId;
  if (action.type === 'project_plan' && typeof quoteId === 'string') {
    return typeof action.payload?.existingProjectId === 'string'
      ? { key: 'other_project', label: 'C’est un autre projet', quoteId }
      : { key: 'ignore_quote', label: 'Pas de projet pour ce devis', quoteId };
  }
  return null;
}

export const ActionClient = z.object({
  id: Uuid,
  name: z.string(),
  axonautCompanyId: z.string().nullable(),
  stripeCustomerId: z.string().nullable(),
  evolusiteSiteId: z.string().nullable(),
  /** Lien vers le site dans l'admin Evolusite (modèle de la connexion du back), si le site est connu. */
  adminSiteUrl: z.string().nullable().optional(),
  /**
   * Lot AI [30] : page du site dans le super admin Evolusite (`<admin>/superAdmin/site/<idSite>`), où le
   * supprimer à la main après « Client parti » (le Cockpit n'écrit jamais dans le back).
   */
  superAdminSiteUrl: z.string().nullable().optional(),
  /** Lot AK : responsable des relances de devis du client (mention sur « Relancer le devis »), null sinon. */
  quoteFollowupOwnerId: Uuid.nullable().optional(),
});
export type ActionClient = z.infer<typeof ActionClient>;

/**
 * Lot AS : une « étape » à cocher d'une carte « Tâche » (`user_task`), dans l'ordre de `position`. Cocher toutes
 * les étapes ne ferme jamais la carte (« Fait » reste un geste de la personne).
 */
export const ActionChecklistItem = z.object({
  id: Uuid,
  label: z.string(),
  done: z.boolean(),
  doneAt: IsoDateTime.nullable(),
  doneBy: Uuid.nullable(),
});
export type ActionChecklistItem = z.infer<typeof ActionChecklistItem>;

/**
 * Lot AS (second temps) : une image d'une carte « Tâche » (5 au plus), lue à l'adresse authentifiée `url`
 * (`GET /api/actions/:id/attachments/:attachmentId`).
 */
export const ActionAttachment = z.object({
  id: Uuid,
  url: z.string(),
  mime: z.enum(FEEDBACK_ATTACHMENT_TYPES),
});
export type ActionAttachment = z.infer<typeof ActionAttachment>;

export const Action = z.object({
  id: Uuid,
  type: ActionType,
  category: ActionCategory,
  origin: ActionOrigin,
  ruleKey: z.string().nullable(),
  /** « relance tous les 10 jours », « tâche ajoutée à la main »… (ligne « Règle : » de la maquette). */
  ruleLabel: z.string().nullable(),
  title: z.string(),
  why: z.string().nullable(),
  amountCents: Cents.nullable(),
  /** « par an », « par mois »… */
  amountNote: z.string().nullable(),
  dueDate: IsoDate,
  assigneeId: Uuid.nullable(),
  status: ActionStatus,
  /** Réponse du client détectée (statut REPLY_TO_HANDLE). */
  replySnippet: z.string().nullable(),
  client: ActionClient.nullable(),
  projectId: Uuid.nullable(),
  stepId: Uuid.nullable(),
  primary: ActionPrimary,
  /** Données propres à la règle (devis, facture, site…), lues par les plans. */
  payload: z.record(z.string(), z.unknown()),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime.nullable(),
  doneAt: IsoDateTime.nullable(),
  /** null pour une action fermée d'elle-même par la synchro (`payload.autoClosed`). */
  doneBy: Uuid.nullable(),
  /** Lot AS : description d'une carte « Tâche » (texte brut, retours à la ligne gardés) ; null ailleurs. */
  description: z.string().nullable().default(null),
  /** Lot AS : étapes à cocher d'une carte « Tâche », dans l'ordre ; vide ailleurs. */
  checklist: z.array(ActionChecklistItem).default([]),
  /** Lot AS : images d'une carte « Tâche », dans l'ordre ; vide ailleurs. */
  attachments: z.array(ActionAttachment).default([]),
});
export type Action = z.infer<typeof Action>;

/** Filtre de statut de `GET /api/actions` : `open` (défaut) = OPEN, WAITING_REPLY et REPLY_TO_HANDLE. */
export const ActionStatusFilter = z.union([z.literal('open'), z.literal('all'), ActionStatus]);
export type ActionStatusFilter = z.infer<typeof ActionStatusFilter>;

/** `GET /api/actions?who=&category=&status=` : `who` = identifiant d'un membre, `me` ou `all` (défaut). */
export const ActionsQuery = z.object({
  who: z.union([z.literal('all'), z.literal('me'), Uuid]).default('all'),
  category: z.union([z.literal('all'), ActionCategory]).default('all'),
  status: ActionStatusFilter.default('open'),
});
export type ActionsQuery = z.input<typeof ActionsQuery>;

/** Lot AS : description d'une tâche (texte brut ; vide = pas de description). */
export const TaskDescription = z.string().max(TASK_DESCRIPTION_MAX);
/** Lot AS : libellé d'une étape. */
export const TaskStepLabel = z.string().trim().min(1).max(TASK_STEP_LABEL_MAX);

/**
 * `POST /api/actions` : tâche ajoutée à la main (`user_task`, origine USER). Lot AS : client sans projet,
 * description et étapes initiales (« Plus de détails »), facultatifs.
 */
export const CreateActionBody = z.object({
  title: z.string().trim().min(1).max(300),
  assigneeId: Uuid,
  dueDate: IsoDate,
  clientId: Uuid.optional(),
  projectId: Uuid.optional(),
  description: TaskDescription.optional(),
  steps: z.array(TaskStepLabel).max(TASK_STEPS_MAX).optional(),
});
export type CreateActionBody = z.infer<typeof CreateActionBody>;

/**
 * `PATCH /api/actions/:id` : déplacer, réattribuer, écarter (interne, sans plan, CDC §3.5).
 * `status: 'OPEN'` rouvre une action faite ou écartée (« Annuler » de la carte « Fait cette semaine »).
 * Lot AS : `title`, `clientId`, `projectId` (null = retirer) et `description` (null ou vide = retirer) se
 * modifient sur une carte « Tâche » (`user_task`) seulement : 422 `ACTION_NOT_EDITABLE` sur les autres cartes.
 */
export const UpdateActionBody = z
  .object({
    dueDate: IsoDate.optional(),
    assigneeId: Uuid.optional(),
    status: z.enum(['DISMISSED', 'OPEN']).optional(),
    title: z.string().trim().min(1).max(300).optional(),
    clientId: Uuid.nullable().optional(),
    projectId: Uuid.nullable().optional(),
    description: TaskDescription.nullable().optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: 'Aucune modification demandée',
  });
export type UpdateActionBody = z.infer<typeof UpdateActionBody>;

/** Champs de `UpdateActionBody` propres aux cartes « Tâche » (lot AS). */
export const TASK_ONLY_FIELDS = ['title', 'clientId', 'projectId', 'description'] as const;

export const ActionIdParams = z.object({ id: Uuid });

// ------------------------------------------------------------------ Étapes d'une tâche (lot AS)

export const ChecklistItemParams = z.object({ id: Uuid, itemId: Uuid });
export const ActionAttachmentParams = z.object({ id: Uuid, attachmentId: Uuid });

/** `POST /api/actions/:id/checklist` : ajoute une étape en bas de la liste. */
export const AddChecklistItemBody = z.object({ label: TaskStepLabel });
export type AddChecklistItemBody = z.infer<typeof AddChecklistItemBody>;

/**
 * `PATCH /api/actions/:id/checklist/:itemId` : une étape à la fois (cocher, renommer, déplacer). `position` =
 * nouvel indice dans la liste (0 = en haut, borné à la fin) ; les autres étapes se décalent.
 */
export const UpdateChecklistItemBody = z
  .object({
    label: TaskStepLabel.optional(),
    done: z.boolean().optional(),
    position: z.int().min(0).max(TASK_STEPS_MAX).optional(),
  })
  .refine((b) => b.label !== undefined || b.done !== undefined || b.position !== undefined, {
    message: 'Aucune modification demandée',
  });
export type UpdateChecklistItemBody = z.infer<typeof UpdateChecklistItemBody>;

/** Lot AS : clients du sélecteur avec recherche d'une tâche (`GET /api/clients/options`, ouvert à tous). */
export const ClientOption = z.object({ id: Uuid, name: z.string() });
export type ClientOption = z.infer<typeof ClientOption>;
export const ClientOptionList = z.object({ items: z.array(ClientOption) });
export type ClientOptionList = z.infer<typeof ClientOptionList>;

/**
 * Lot X : réponse de `POST /api/actions/:id/skip-r2` (« Pas de R2 pour ce client ») : la carte « Fixer le R2 »
 * faite et les cartes changées par le passage des règles qui suit (« Envoyer le devis » ajoutée).
 */
export const SkipR2Result = z.object({ action: Action, consequences: z.array(ActionConsequence) });
export type SkipR2Result = z.infer<typeof SkipR2Result>;

// ------------------------------------------------------------------ Chance de signature (lot BL)

/** Lot BL : puces de la carte « Chance de signature » (en %), plus « Je ne sais pas encore » (null). */
export const WIN_CHANCE_CHOICES = [10, 30, 50, 70, 90] as const;

/** Chance de signature d'un devis : 0 à 100 %, null = inconnue. */
export const WinChance = z.int().min(0).max(100).nullable();

/**
 * Lot BL : `POST /api/actions/:id/win-chance` : la carte « Chance de signature » d'un clic (geste interne, sans
 * plan). `winChance` null = « Je ne sais pas encore » : la carte est faite, le devis garde sa chance inconnue.
 */
export const WinChanceBody = z.object({ winChance: WinChance });
export type WinChanceBody = z.infer<typeof WinChanceBody>;
/** Réponse : la carte faite et les cartes changées par les règles qui suivent (cartes des autres membres fermées). */
export const WinChanceResult = SkipR2Result;
export type WinChanceResult = SkipR2Result;

// ------------------------------------------------------------------ Pas d'abonnement à mettre (lot AI [30])

/**
 * Raison de « Pas d'abonnement à mettre » sur « Mettre en place l'abonnement » : `none` client sans abonnement
 * (la carte ne revient plus, mention sur sa fiche, réversible) ; `left` client parti à une date (compté dans
 * « Clients perdus » de Chiffres) ; `scheduled` abonnement programmé à une date (carte en veille jusqu'à la date
 * + 3 jours, de retour seulement si aucun abonnement n'est apparu).
 */
export const NoSubscriptionReason = z.enum(['none', 'left', 'scheduled']);
export type NoSubscriptionReason = z.infer<typeof NoSubscriptionReason>;

export const NO_SUBSCRIPTION_LABELS: Record<NoSubscriptionReason, string> = {
  none: 'Client sans abonnement',
  left: 'Client parti',
  scheduled: 'Abonnement programmé',
};

/** `POST /api/actions/:id/no-subscription` (geste interne, sans plan, comme « Écarter »). */
export const NoSubscriptionBody = z.discriminatedUnion('reason', [
  z.object({ reason: z.literal('none') }),
  z.object({ reason: z.literal('left'), date: IsoDate }),
  z.object({ reason: z.literal('scheduled'), date: IsoDate }),
]);
export type NoSubscriptionBody = z.infer<typeof NoSubscriptionBody>;

/** Réponse : la carte faite et les cartes changées par le passage des règles qui suit. */
export const NoSubscriptionResult = z.object({ action: Action, consequences: z.array(ActionConsequence) });
export type NoSubscriptionResult = z.infer<typeof NoSubscriptionResult>;

/** Mention « Pas d'abonnement à mettre » d'un client (fiche du projet, Réglages). */
export const ClientNoSubscription = z.object({
  clientId: Uuid,
  clientName: z.string(),
  reason: NoSubscriptionReason,
  /** Date de départ (`left`) ou de début prévu (`scheduled`) ; null pour `none`. */
  on: IsoDate.nullable(),
  /** `scheduled` : jour où la carte revient si aucun abonnement n'est apparu (date + 3 jours). */
  wakeOn: IsoDate.nullable(),
  at: IsoDateTime,
  byName: z.string().nullable(),
  superAdminSiteUrl: z.string().nullable(),
});
export type ClientNoSubscription = z.infer<typeof ClientNoSubscription>;

/** `GET /api/clients/no-subscription` : clients marqués, du plus récent au plus ancien. */
export const ClientNoSubscriptionList = z.object({ items: z.array(ClientNoSubscription) });
export type ClientNoSubscriptionList = z.infer<typeof ClientNoSubscriptionList>;

/** `DELETE /api/clients/:id/no-subscription` : mention retirée, la carte fermée par ce choix rouverte. */
export const ClearNoSubscriptionResult = z.object({
  reopenedActionId: Uuid.nullable(),
  /** Lot BR : de quoi annuler (`POST …/no-subscription/restore`) ; null : rien n'a changé. */
  undoId: Uuid.nullable(),
});
export type ClearNoSubscriptionResult = z.infer<typeof ClearNoSubscriptionResult>;

/** « Client sans abonnement · choisi le 14 oct. par Maxime », « Client parti le 30 sept. »… */
export function noSubscriptionText(m: Pick<ClientNoSubscription, 'reason' | 'on' | 'wakeOn'>): string {
  if (m.reason === 'left') return m.on ? `Client parti le ${formatShortFr(m.on)}` : 'Client parti';
  if (m.reason === 'scheduled') {
    const start = m.on ? ` le ${formatShortFr(m.on)}` : '';
    const wake = m.wakeOn ? ` · carte de retour le ${formatShortFr(m.wakeOn)} s’il n’est pas en place` : '';
    return `Abonnement programmé${start}${wake}`;
  }
  return 'Client sans abonnement';
}

/** `GET /api/actions/done?since=` : actions faites depuis cette date (défaut : lundi de la semaine). */
export const ActionsDoneQuery = z.object({ since: IsoDate.optional() });
export type ActionsDoneQuery = z.infer<typeof ActionsDoneQuery>;

/** `GET /api/actions/stakes` : carte « Ce qui est en jeu » de la maquette. */
export const StakeKey = z.enum(['invoice', 'quote_followup', 'unpaid', 'subscription']);
export type StakeKey = z.infer<typeof StakeKey>;
export const ActionStake = z.object({
  key: StakeKey,
  /** « à facturer », « de devis à relancer », « à récupérer », « d'abonnement par an » */
  label: z.string(),
  /** « 3 factures à créer », « 2 devis »… */
  detail: z.string(),
  amountCents: Cents,
  count: z.int().min(0),
});
export type ActionStake = z.infer<typeof ActionStake>;
export const ActionStakes = z.object({ items: z.array(ActionStake) });
export type ActionStakes = z.infer<typeof ActionStakes>;

/** `POST /api/actions/refresh` : synchro Gmail (si Google est connecté) puis règles, à la demande. */
export const RulesRunResult = z.object({
  created: z.int().min(0),
  updated: z.int().min(0),
  closed: z.int().min(0),
  replies: z.int().min(0),
  gmail: z.record(z.string(), z.int()).nullable(),
});
export type RulesRunResult = z.infer<typeof RulesRunResult>;

// ------------------------------------------------------------------ Échanges avec un client

export const ClientMessage = z.object({
  id: Uuid,
  source: z.enum(['gmail', 'axonaut']),
  direction: z.enum(['in', 'out']),
  byLabel: z.string().nullable(),
  userId: Uuid.nullable(),
  subject: z.string().nullable(),
  snippet: z.string().nullable(),
  sentAt: IsoDateTime,
  threadId: z.string().nullable(),
  /** Lot L : boîte partagée où le message a été lu (« via contact@ »), null pour la boîte d'un membre. */
  mailbox: z.string().nullable(),
});
export type ClientMessage = z.infer<typeof ClientMessage>;

/**
 * `GET /api/clients/:id/messages` : échanges du plus récent au plus ancien. `waitingOn` : `client` si le
 * dernier message est de l'agence (on attend le client), `us` s'il vient du client, null sans échange.
 */
export const ClientMessages = z.object({
  clientId: Uuid,
  waitingOn: z.enum(['client', 'us']).nullable(),
  items: z.array(ClientMessage),
});
export type ClientMessages = z.infer<typeof ClientMessages>;

export const ClientIdParams = z.object({ id: Uuid });

// ------------------------------------------------------------------ Brouillons de mails (IA)

export const DraftTone = z.enum(['cordial', 'direct', 'last']);
export type DraftTone = z.infer<typeof DraftTone>;

/** `POST /api/drafts/email` : brouillon rédigé par Claude (ou modèle de la maquette en mode fake). */
export const DraftEmailBody = z.object({ actionId: Uuid, tone: DraftTone.default('cordial') });
export type DraftEmailBody = z.input<typeof DraftEmailBody>;

export const DraftEmail = z.object({
  to: z.array(z.string()),
  subject: z.string(),
  body: z.string(),
  tone: DraftTone,
  /** `ai` : rédigé par Claude ; `template` : modèle de la maquette (mode fake ou repli). */
  source: z.enum(['ai', 'template']),
});
export type DraftEmail = z.infer<typeof DraftEmail>;

/** Ton proposé par défaut (maquette : « dernière relance » au-delà de 45 jours pour un devis). */
export function defaultTone(type: ActionType, daysSinceSent: number | null): DraftTone {
  return type === 'quote_followup' && daysSinceSent !== null && daysSinceSent > 45 ? 'last' : 'cordial';
}

// ------------------------------------------------------------------ Événement SSE

/** Événement SSE `action.changed` (`GET /api/stream`) : la liste À faire est à relire. */
export const ActionChangedEvent = z.object({
  actionIds: z.array(Uuid),
  reason: z.enum(['rules', 'reply', 'created', 'updated', 'plan']),
});
export type ActionChangedEvent = z.infer<typeof ActionChangedEvent>;

// ------------------------------------------------------------------ Libellés de dates

const WEEKDAYS_SHORT = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];
const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

/** « aujourd'hui », « demain », « mar. 20 oct. » (maquette, `fmtDay`). */
export function formatDayFr(date: string, today: string): string {
  const offset = diffDays(today, date);
  if (offset === 0) return "aujourd'hui";
  if (offset === 1) return 'demain';
  const { day, month } = parseIsoDate(date);
  return `${WEEKDAYS_SHORT[isoWeekday(date) - 1]} ${day} ${MONTHS_SHORT[month - 1]}`;
}

/** « En retard de 3 j », « Pour aujourd'hui », « Pour demain », « Pour mar. 20 oct. » (maquette, `dueLabel`). */
export function dueLabel(dueDate: string, today: string): string {
  const offset = diffDays(today, dueDate);
  if (offset < 0) return `En retard de ${-offset} j`;
  if (offset === 0) return "Pour aujourd'hui";
  if (offset === 1) return 'Pour demain';
  return `Pour ${formatDayFr(dueDate, today)}`;
}

/** Groupe de la liste (maquette, `groupOf`) : en retard, aujourd'hui, cette semaine (6 j), plus tard. */
export function dueGroup(dueDate: string, today: string): 'late' | 'today' | 'week' | 'later' {
  const offset = diffDays(today, dueDate);
  if (offset < 0) return 'late';
  if (offset === 0) return 'today';
  return offset <= 6 ? 'week' : 'later';
}
